import { Box, Button, Tag, Text } from '@nimbus-ds/components';
import { InitialScreen } from '@nimbus-ds/patterns';
import { Trans, useTranslation } from 'react-i18next';
import { useResponsive } from 'commons/components';
import { NUVEM_CHAT_APP_INSTALL_LINK } from 'commons/constants/externalLinks';
import { useTranslationLanguage } from 'commons/hooks';
import { openWindow } from 'commons/utils/window';
import { NuvemChatPlans } from './components/NuvemChatPlans';
import NuvemChatImageLanding from './images/nuvem-chat-image-landing.png';
import NuvemChatImagePt from './images/nuvem-chat-image-pt.png';
import NuvemChatImageSmPt from './images/nuvem-chat-image-sm-pt.png';
import NuvemChatImageSm from './images/nuvem-chat-image-sm.png';
import NuvemChatImage from './images/nuvem-chat-image.png';
import {
  trackingNuvemChatInfoClick,
  trackingNuvemChatInstallClick,
} from '../tracking';
import './NuvemChatPage.scss';

export function NuvemChatPage() {
  const { t } = useTranslation('nuvemchat');
  const language = useTranslationLanguage();
  const { isMobile } = useResponsive();

  const linksInfoChat = {
    'es-AR': 'https://www.tiendanube.com/soluciones/chat-nube',
    'es-MX': 'https://www.tiendanube.com/mx/soluciones/chat-nube',
    'es-CO': 'https://www.tiendanube.com/co/soluciones/chat-nube',
    'es-CL': 'https://www.tiendanube.com/cl/soluciones/chat-nube',
    'pt-BR': 'https://www.nuvemshop.com.br/solucoes/nuvem-chat',
  };
  const handleInstallApplicationClick = () => {
    const url = NUVEM_CHAT_APP_INSTALL_LINK[language];
    trackingNuvemChatInstallClick();
    openWindow(url, true);
  };

  const handleInfoChatClick = () => {
    trackingNuvemChatInfoClick();
    const infoUrl =
      linksInfoChat[language] ??
      (language?.startsWith('pt')
        ? linksInfoChat['pt-BR']
        : linksInfoChat['es-AR']);
    openWindow(infoUrl, true);
  };

  const chatView = language === 'pt-BR' ? NuvemChatImagePt : NuvemChatImage;
  const chatViewSm =
    language === 'pt-BR' ? NuvemChatImageSmPt : NuvemChatImageSm;
  const responsiveSizes = isMobile
    ? {
        imgSrc: chatViewSm,
        boxMaxWidth: '500px',
      }
    : {
        imgSrc: chatView,
        boxMaxWidth: '600px',
        boxMaxHeight: '500px',
      };

  const renderImage = () => (
    <Box
      maxWidth={responsiveSizes.boxMaxWidth}
      maxHeight={responsiveSizes.boxMaxHeight}
      borderRadius="6"
      borderColor="primary-interactive"
      borderWidth="4"
      borderStyle="solid"
    >
      <img
        src={responsiveSizes.imgSrc}
        alt="NuvemChatView"
        width="100%"
        style={{ borderRadius: '20px' }}
      />
    </Box>
  );
  return (
    <InitialScreen mx="auto">
      <InitialScreen.Hero
        image={
          <img
            src={NuvemChatImageLanding}
            alt="NuvemChat"
            width="100%"
            height="auto"
          />
        }
        title={t('hero.title')}
        subtitle={t('hero.subtitle').toUpperCase()}
        description={
          <Text fontSize="highlight" color="neutral-textHigh" textAlign="left">
            <Trans
              t={t}
              i18nKey="hero.description"
              components={{ bold: <strong /> }}
            />
          </Text>
        }
        actions={
          <>
            <Button
              appearance="primary"
              onClick={handleInstallApplicationClick}
            >
              {t('hero.button')}
            </Button>
            <Button appearance="neutral" onClick={handleInfoChatClick}>
              {t('hero.button2')}
            </Button>
          </>
        }
      />
      <InitialScreen.Section title={t('contentSection.title')}>
        <InitialScreen.Feature
          content={
            <>
              <InitialScreen.FeatureItem
                title={t('contentSection.content1.title')}
                description={t('contentSection.content1.description')}
              >
                <InitialScreen.FeatureItemSpacing />
              </InitialScreen.FeatureItem>

              <InitialScreen.FeatureItem
                title={t('contentSection.content2.title')}
                description={t('contentSection.content2.description')}
              >
                <InitialScreen.FeatureItemSpacing />
              </InitialScreen.FeatureItem>
              <InitialScreen.FeatureItem
                title={t('contentSection.content3.title')}
                description={t('contentSection.content3.description')}
              />
              <InitialScreen.FeatureItemSpacing />
              <InitialScreen.FeatureItem
                title={t('contentSection.content4.title')}
                description={t('contentSection.content4.description')}
              />
            </>
          }
          image={
            language === 'pt-BR' ? (
              <Box
                display="flex"
                flexDirection="column"
                gap="2"
                alignItems="center"
              >
                {renderImage()}
                <Tag appearance="primary">{t('contentSection.tag')}</Tag>
              </Box>
            ) : (
              renderImage()
            )
          }
        />
      </InitialScreen.Section>
      <InitialScreen.Section title={t('plans.title')}>
        <NuvemChatPlans />
      </InitialScreen.Section>
    </InitialScreen>
  );
}
